import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BellRing, CalendarClock, ChevronDown, FileText, MessageSquare, Package, PackageSearch, Receipt } from "lucide-react";
import { PageHeader } from "@/components/common";
import { FinanceBlock, UnpaidCard } from "@/modules/dashboards/FinanceBlock";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { t } from "@/i18n";

export function HomeBlock({ title, testId, collapsible, children }) {
  const key = `gf_home_${testId}`;
  const [open, setOpen] = useState(() => !collapsible || localStorage.getItem(key) !== "0");
  const toggle = () => setOpen((o) => { localStorage.setItem(key, o ? "0" : "1"); return !o; });
  return (
    <section className="space-y-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{title}</h2>
        <span className="h-px flex-1 bg-slate-200" />
        {collapsible && <button type="button" onClick={toggle} aria-expanded={open} className="inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid={`${testId}-toggle`}>
          {open ? t("b13.hide") : t("b13.show")}<ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} /></button>}
      </div>
      {open && <div className="space-y-4">{children}</div>}
    </section>
  );
}

function Todo({ icon: Icon, n, label, to, testId, warn }) {
  if (n == null) return null;
  return (
    <Link to={to} data-testid={testId}
      className={`gf-card group flex min-h-16 items-center gap-3 p-4 transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-md ${n === 0 ? "opacity-60" : ""}`}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${n > 0 && warn ? "bg-amber-100 text-amber-700" : "bg-teal-50 text-teal-700"}`}><Icon className="h-5 w-5" /></span>
      <span className="min-w-0 flex-1 text-sm font-medium text-slate-700">{label}</span>
      <span className="font-display text-2xl font-extrabold text-slate-900" data-testid={`${testId}-count`}>{n}</span>
    </Link>
  );
}

export function TodoBlock() {
  const { hasFeature, isManager } = useGarage();
  const billing = hasFeature("quotes_and_invoices") && isManager;
  const today = useFetch("/garage/today");
  const drafts = useFetch(billing ? "/quotes" : null, { status: "draft" });
  const unpaid = useFetch(billing ? "/invoices" : null, { status: "unpaid" });
  const fu = useFetch(hasFeature("follow_ups") && isManager ? "/followups" : null);
  const low = useFetch(hasFeature("inventory") ? "/inventory/low-stock/count" : null);
  const msgs = useFetch(hasFeature("messaging") ? "/messages/unread" : null);
  const pend = today.data?.pending || [];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="todo-list">
      <Todo icon={CalendarClock} n={today.data && pend.filter((a) => a.status === "pending").length} label={t("b13.todo.requests")} to="/pro/planning" testId="todo-requests" warn />
      <Todo icon={PackageSearch} n={today.data && pend.filter((a) => a.status === "awaiting_parts").length} label={t("b13.todo.parts")} to="/pro/planning" testId="todo-parts" warn />
      <Todo icon={FileText} n={drafts.data?.length} label={t("b13.todo.quotes")} to="/pro/quotes?status=draft" testId="todo-quotes" />
      <Todo icon={Receipt} n={unpaid.data?.length} label={t("b13.todo.unpaid")} to="/pro/invoices?status=unpaid" testId="todo-unpaid" warn />
      <Todo icon={BellRing} n={fu.data && fu.data.summary.overdue + fu.data.summary.to_follow_up} label={t("b13.todo.followups")} to="/pro/followups" testId="todo-followups" />
      <Todo icon={Package} n={low.data?.count} label={t("b13.todo.lowStock")} to="/pro/inventory?tab=parts&low=1" testId="todo-low-stock" warn />
      <Todo icon={MessageSquare} n={msgs.data?.conversations} label={t("b13.todo.messages")} to="/pro/messages" testId="todo-messages" />
    </div>
  );
}

export default function Finance() {
  const { hasFeature, isManager } = useGarage();
  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader title={t("b13.nav.finance")} subtitle={t("b13.financeHint")} />
      {hasFeature("quotes_and_invoices") && isManager ? <><UnpaidCard /><FinanceBlock /></>
        : <p className="flex items-center gap-2 text-sm text-slate-500"><AlertTriangle className="h-4 w-4" />{t("b13.financeNa")}</p>}
    </div>
  );
}
